import {
  NAVIGATION_LOAD_MORE_SIZE,
  NAVIGATION_PAGE_SIZE,
  type NavigationGroup,
} from "./navigation-items";
import type { PreviewLocalization } from "./preview-localization";

/**
 * The `@inContext(country, language)` variables. Every Navigator query takes
 * them from the Preview localization: a query without them resolves an
 * arbitrary country. The locale root is not a variable; it goes into the paths.
 */
export interface InContextVariables {
  country: PreviewLocalization["country"];
  language: PreviewLocalization["language"];
}

/** The variables of the first page of each group when there is no query. */
export interface BrowseVariables extends InContextVariables {
  first: number;
}

/** The variables of the next page of one group when there is no query. */
export interface LoadMoreVariables extends InContextVariables {
  first: number;
  /** The end cursor of the group's items so far. */
  after: string;
}

function inContextVariables({
  country,
  language,
}: PreviewLocalization): InContextVariables {
  return { country, language };
}

/**
 * The variables for the first page of every group with no query. It asks for
 * one item more than a page, so that `buildNavigationItems` can tell that a
 * group has more.
 */
export function buildBrowseVariables(
  localization: PreviewLocalization
): BrowseVariables {
  return {
    first: NAVIGATION_PAGE_SIZE + 1,
    ...inContextVariables(localization),
  };
}

/**
 * The variables for the next page of one group with no query. It continues
 * after the group's cursor, with the same `@inContext` as the first page, and
 * asks for one item more than a load-more page, so that
 * `addLoadMorePage` can tell that the group has more.
 */
export function buildLoadMoreVariables(
  localization: PreviewLocalization,
  after: string
): LoadMoreVariables {
  return {
    first: NAVIGATION_LOAD_MORE_SIZE + 1,
    after,
    ...inContextVariables(localization),
  };
}

/**
 * The groups that the Storefront `search` query returns, with cursors.
 * `search` does not accept collections.
 */
export type SearchGroup = Exclude<NavigationGroup, "collections">;

/** The Storefront `SearchType` of each group that `search` returns. */
const SEARCH_TYPES = {
  pages: "PAGE",
  products: "PRODUCT",
  articles: "ARTICLE",
} as const satisfies Record<SearchGroup, string>;

export type StorefrontSearchType = (typeof SEARCH_TYPES)[SearchGroup];

/** The path prefix that limits a typed search to one group when a term follows it. */
const PREFIX_GROUPS: Record<string, NavigationGroup> = {
  products: "products",
  collections: "collections",
  pages: "pages",
  blogs: "articles",
};

export interface NavigationInput {
  /** The text to search for. */
  term: string;
  /**
   * The group that a `<prefix>/<term>` input limits the search to. Absent when
   * no term follows the prefix.
   */
  group?: NavigationGroup;
}

/**
 * Splits the typed input into a text term and an optional group. The prefixes
 * are `products/`, `collections/`, `pages/` and `blogs/`. A prefix limits the
 * search to its group only when a term follows it: `products/shirt` is a text
 * search for `shirt` in products (a text search, not a handle match). A prefix
 * with no term (`products`, `products/`, `/products/`) does not limit the
 * search: it is a text search in every group for the input as typed, as is any
 * other input. The Navigator used this grammar before the Storefront search.
 */
export function parseNavigationInput(input: string): NavigationInput {
  const trimmed = input.replace(/^\/+|\/+$/g, "");
  for (const [prefix, group] of Object.entries(PREFIX_GROUPS)) {
    if (trimmed.startsWith(`${prefix}/`)) {
      const term = trimmed.slice(prefix.length + 1);
      if (term) {
        return { term, group };
      }
    }
  }
  return { term: input };
}

/** The variables of a typed search in every group. */
export interface SearchVariables extends InContextVariables {
  /** The typed text, searched in the preview's language. */
  query: string;
  first: number;
}

/** The variables of a typed search in one `search` group. */
export interface SearchGroupVariables extends SearchVariables {
  types: [StorefrontSearchType];
  /** The end cursor of the group's items so far. Absent for the first page. */
  after?: string;
}

/** The variables of a typed search in collections (`predictiveSearch`). */
export interface CollectionSearchVariables extends InContextVariables {
  query: string;
}

/**
 * Which typed search to send, and its variables:
 * - `allGroups`: `search` for pages, products and articles, and
 *   `predictiveSearch` for collections;
 * - `group`: `search` in one group;
 * - `collections`: `predictiveSearch` for collections only.
 */
export type NavigationSearch =
  | { kind: "allGroups"; variables: SearchVariables }
  | { kind: "group"; group: SearchGroup; variables: SearchGroupVariables }
  | { kind: "collections"; variables: CollectionSearchVariables };

/**
 * The typed search for the merchant's input. It asks for one item more than a
 * page, so that `buildSearchItems` can tell that a group has more.
 * `predictiveSearch` takes no page size and gives no "load more".
 */
export function buildSearchVariables(
  localization: PreviewLocalization,
  input: string
): NavigationSearch {
  const { term, group } = parseNavigationInput(input);
  const inContext = inContextVariables(localization);
  if (group === "collections") {
    return { kind: "collections", variables: { query: term, ...inContext } };
  }
  const first = NAVIGATION_PAGE_SIZE + 1;
  if (group) {
    return {
      kind: "group",
      group,
      variables: {
        query: term,
        types: [SEARCH_TYPES[group]],
        first,
        ...inContext,
      },
    };
  }
  return { kind: "allGroups", variables: { query: term, first, ...inContext } };
}

/**
 * The variables for the next page of one `search` group of a typed search. It
 * continues after the group's cursor, with the same term and `@inContext` as
 * the first page, and asks for one item more than a load-more page, so that
 * `addLoadMorePage` can tell that the group has more. Collections have no
 * "load more".
 */
export function buildSearchLoadMoreVariables(
  localization: PreviewLocalization,
  input: string,
  group: SearchGroup,
  after: string
): SearchGroupVariables {
  return {
    query: parseNavigationInput(input).term,
    types: [SEARCH_TYPES[group]],
    first: NAVIGATION_LOAD_MORE_SIZE + 1,
    after,
    ...inContextVariables(localization),
  };
}
